import React, { useState } from "react";
import { useQuery, useMutation } from "react-query";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Typography,
  Checkbox,
  Chip,
  Tab,
  Tabs,
  Card,
  CardContent,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  CircularProgress,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import FilterListIcon from "@mui/icons-material/FilterList";
import client from "../../../../API";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";
import DebtDialog from "./Withdrawals/debtdialog";
import LiquidityPoolDialog from "./systemcomps/liquiditypooldialog";
import InvoiceDialog from "../invoicedialog";
import CampaignDialog from "../campaigndialog";
import PartnershipDialog from "./editingcollabs/partnershipdialog";
import DeliverableDialog from "./editingcollabs/deliverabledialog";
import { MetricCard, FinancialCard } from "./systemcomps/SysCards";
import AccountsReceivableDialog from "./systemcomps/SysARDialog";
import CollabsRevenueDialog from "./systemcomps/CollabsRevenueDialog";
import PendingProfitsDialog from "./systemcomps/PendingProfitsDialog";
import {
  InvoicesTable,
  CollabsTable,
  DeliverablesTable,
} from "./systemcomps/SysDataTables";
import {
  exportToCSV,
  prepareInvoicesForExport,
  prepareCollabsForExport,
  prepareDeliverablesForExport,
} from "./systemcomps/sysexportutils";

const LIQUIDITY_RESERVE_PERCENT = 0.2;
const DEBT_RESERVE_PERCENT = 0.1;

const BlitzSystemOverview = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [debtDialogOpen, setDebtDialogOpen] = useState(false);
  const [liquidityPoolDialogOpen, setLiquidityPoolDialogOpen] = useState(false);
  const { dialogState, openDialog } = useAlertDialog();
  const [searchTerm, setSearchTerm] = useState("");

  // Selection states
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedCollabs, setSelectedCollabs] = useState([]);
  const [selectedDeliverables, setSelectedDeliverables] = useState([]);

  // Dialog states
  const [isInvoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [isCampaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isPartnershipDialogOpen, setPartnershipDialogOpen] = useState(false);
  const [editingPartnership, setEditingPartnership] = useState(null);
  const [isDeliverableDialogOpen, setDeliverableDialogOpen] = useState(false);
  const [editingDeliverable, setEditingDeliverable] = useState(null);

  // Accounts Receivable dialog states
  const [isARDialogOpen, setARDialogOpen] = useState(false);
  const [selectedARInvoices, setSelectedARInvoices] = useState([]);
  const [arSearchTerm, setARSearchTerm] = useState("");
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState(null);
  const [arDialogMode, setARDialogMode] = useState("blitzpay");

  // New dialog states
  const [isCollabsRevenueDialogOpen, setCollabsRevenueDialogOpen] =
    useState(false);
  const [isPendingProfitsDialogOpen, setPendingProfitsDialogOpen] =
    useState(false);
  const [isSpendingPowerDialogOpen, setSpendingPowerDialogOpen] =
    useState(false);

  // Filter states
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("nonCanceled");
  const [collabTypeFilter, setCollabTypeFilter] = useState("all");
  const [collabStatusFilter, setCollabStatusFilter] = useState("all");

  // Pagination states
  const [invoicesPage, setInvoicesPage] = useState(0);
  const [invoicesPerPage, setInvoicesPerPage] = useState(20);
  const [collabsPage, setCollabsPage] = useState(0);
  const [collabsPerPage, setCollabsPerPage] = useState(20);

  // Queries
  const { data: companies = [], isLoading: companiesLoading } = useQuery(
    ["companies"],
    client.companies.list
  );

  // System metrics from backend
  const {
    data: systemMetrics,
    refetch: refetchMetrics,
    isLoading: metricsLoading,
  } = useQuery(
    ["systemMetrics"],
    client.invoiceCollections.systemOverview.getMetrics,
    {
      refetchOnWindowFocus: false,
    }
  );

  // PAGINATED queries for table display
  const {
    data: invoicesData,
    refetch: refetchInvoices,
    isLoading: invoicesLoading,
  } = useQuery(
    ["invoices", invoicesPage, invoicesPerPage],
    () => client.invoices.listAdmin({ page: invoicesPage, rows: invoicesPerPage })
  );

  const {
    data: collaborationData,
    refetch: refetchCollaborations,
    isLoading: collabsLoading,
  } = useQuery({
    queryKey: ["collaborations", collabsPage, collabsPerPage],
    queryFn: () => client.campaigns.getAdminCollaborations({ page: collabsPage, rows: collabsPerPage }),
    refetchOnWindowFocus: false,
  });

  // COMPREHENSIVE queries for dialogs and metrics
  const {
    data: comprehensiveInvoicesData,
    refetch: refetchComprehensiveInvoices,
    isLoading: comprehensiveInvoicesLoading,
  } = useQuery(
    ["comprehensiveInvoices"],
    () => client.invoices.listAdmin({ page: 0, rows: 9999, all: true }),
    {
      refetchOnWindowFocus: false,
      staleTime: 60000, // Cache for 1 minute
    }
  );

  // Update the comprehensive collabs query to include 'all: true'
const {
  data: comprehensiveCollabsData,
  refetch: refetchComprehensiveCollabs,
  isLoading: comprehensiveCollabsLoading,
} = useQuery(
  ["comprehensiveCollabs"],
  () => client.campaigns.getAdminCollaborations({ page: 0, rows: 9999, all: true }), // ADD all: true
  {
    refetchOnWindowFocus: false,
    staleTime: 60000,
  }
);

  const {
    data: liquidityPools = [],
    refetch: refetchLiquidityPools,
    isLoading: liquidityPoolsLoading,
  } = useQuery({
    queryKey: ["liquidityPools"],
    queryFn: () => client.creditLedger.liquidityPools.list(),
    refetchOnWindowFocus: false,
  });

  const {
    data: debtInstruments = [],
    refetch: fetchDebtInstruments,
    isLoading: debtLoading,
  } = useQuery({
    queryKey: ["debtInstruments"],
    queryFn: () => client.creditLedger.debtInstrument.list(),
    refetchOnWindowFocus: false,
  });

  // Mutations
  const { mutate: createDebt } = useMutation(
    client.creditLedger.debtInstrument.create,
    {
      onSuccess: () => {
        fetchDebtInstruments();
        refetchMetrics();
        openDialog("Success", "Debt instrument created");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: updateDebt } = useMutation(
    ({ id, payload }) => client.creditLedger.debtInstrument.update(id, payload),
    {
      onSuccess: () => {
        fetchDebtInstruments();
        refetchMetrics();
        openDialog("Success", "Debt instrument updated");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: createLiquidityPool } = useMutation(
    client.creditLedger.liquidityPools.create,
    {
      onSuccess: () => {
        refetchLiquidityPools();
        refetchMetrics();
        openDialog("Success", "Liquidity pool created");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: updateLiquidityPool } = useMutation(
    ({ id, payload }) => client.creditLedger.liquidityPools.update(id, payload),
    {
      onSuccess: () => {
        refetchLiquidityPools();
        refetchMetrics();
        openDialog("Success", "Liquidity pool updated");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: deleteLiquidityPool } = useMutation(
    (id) => client.creditLedger.liquidityPools.delete(id),
    {
      onSuccess: () => {
        refetchLiquidityPools();
        refetchMetrics();
        openDialog("Success", "Liquidity pool deleted");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: deleteInvoices } = useMutation(
    (invoiceIds) => client.invoices.delete({ invoiceIds }),
    {
      onSuccess: () => {
        refetchInvoices();
        refetchComprehensiveInvoices();
        refetchMetrics();
        setSelectedInvoices([]);
        setSelectedARInvoices([]);
        openDialog("Success", "Invoices deleted");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: deleteCampaigns } = useMutation(
    (campaignIds) => client.campaigns.deleteCampaignAdmin({ campaignIds }),
    {
      onSuccess: () => {
        refetchCollaborations();
        refetchComprehensiveCollabs();
        refetchMetrics();
        setSelectedCollabs([]);
        openDialog("Success", "Campaigns deleted");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: deletePartnerships } = useMutation(
    (partnershipIds) => client.partnerships.delete({ partnershipIds }),
    {
      onSuccess: () => {
        refetchCollaborations();
        refetchComprehensiveCollabs();
        refetchMetrics();
        setSelectedCollabs([]);
        openDialog("Success", "Partnerships deleted");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  const { mutate: deleteDeliverables } = useMutation(
    (deliverableIds) =>
      client.deliverables.deleteDeliverablesAdmin({ deliverableIds }),
    {
      onSuccess: () => {
        refetchCollaborations();
        refetchComprehensiveCollabs();
        refetchMetrics();
        setSelectedDeliverables([]);
        openDialog("Success", "Deliverables deleted");
      },
      onError: (e) =>
        openDialog("Error", e?.response?.data?.error || e.message),
    }
  );

  // Computations / safe arrays for PAGINATED data (tables)
  const companiesArray = Array.isArray(companies)
    ? companies
    : companies?.companies || [];
  const invoicesArray = invoicesData?.invoices || [];
  const totalInvoices = invoicesData?.total || 0;
  const campaignsArray = collaborationData?.campaigns || [];
  const partnershipsArray = collaborationData?.partnerships || [];
  const deliverablesArray = collaborationData?.deliverables || [];
  const totalCampaigns = collaborationData?.totals?.campaigns || 0;
  const totalPartnerships = collaborationData?.totals?.partnerships || 0;
  const totalCollabs = collaborationData?.totals?.collabs || 0;
  const totalDeliverables = collaborationData?.totals?.deliverables || 0;
  const liquidityPoolsArray = Array.isArray(liquidityPools)
    ? liquidityPools
    : [];

  // Computations for COMPREHENSIVE data (dialogs)
  const comprehensiveInvoicesArray = comprehensiveInvoicesData?.invoices || [];
  const comprehensiveCampaignsArray = comprehensiveCollabsData?.campaigns || [];
  const comprehensivePartnershipsArray = comprehensiveCollabsData?.partnerships || [];

  // Combine campaigns and partnerships into collabs array for TABLES
  const collabsArray = [
    ...campaignsArray.map((c) => ({ ...c, type: "Campaign" })),
    ...partnershipsArray.map((p) => ({ ...p, type: "Partnership" })),
  ];

  // Extract metrics from backend response (with fallbacks)
  // Extract metrics from backend response (with fallbacks)
const totalLiquidity = systemMetrics?.reserve_liquidity || 0;
const spendingPowerAllocated = systemMetrics?.spending_power_allocated || 0;
const spendingPowerAvailable = systemMetrics?.available_credit || 0;
const debtOutstanding = systemMetrics?.debt_outstanding || systemMetrics?.debt_in_service || 0;
const debtCapacity = systemMetrics?.debt_capacity || 0;

// FIX: Access nested objects correctly
const pendingInvoicesSumTotal = systemMetrics?.blitzpay_ar?.total || 0;
const pendingInvoicesCountTotal = systemMetrics?.blitzpay_ar?.count || 0;
const projectInvoicesSumTotal = systemMetrics?.collabs_invoices?.total || 0;
const projectInvoicesCountTotal = systemMetrics?.collabs_invoices?.count || 0;
const collabsRevenue = systemMetrics?.collabs_revenue?.total || 0;
const collabsCount = systemMetrics?.collabs_revenue?.count || 0;
const pendingProfits = systemMetrics?.pending_profits?.total || 0;

const overleveraged = systemMetrics?.health_metrics?.overleveraged || 0;
const overleveragedIsNegative = systemMetrics?.health_metrics?.overleveraged_is_negative || overleveraged < 0;
const liquidityBuffer = systemMetrics?.health_metrics?.liquidity_buffer || 0;
const debtBuffer = systemMetrics?.health_metrics?.debt_buffer || 0;
const spendingUtilization = systemMetrics?.health_metrics?.credit_utilization || 0;
const debtUtilization = systemMetrics?.health_metrics?.debt_utilization || 0;

  // For dialog displays, filter from COMPREHENSIVE data
  const blitzpayCompanies = companiesArray.filter((c) => c.blitzpay_account);
  const companiesWithSpendingPower = blitzpayCompanies.filter(
    (c) =>
      c.spending_power_limit &&
      parseFloat(c.spending_power_limit) > 0
  );

  // For AR dialogs - filter from COMPREHENSIVE invoices
  const pendingInvoices = comprehensiveInvoicesArray.filter(
    (inv) =>
      (inv.status === "pending" || inv.status === "approved") &&
      inv.company_info?.name !== "TCC"
  );

  const projectInvoices = comprehensiveInvoicesArray.filter(
    (inv) =>
      (inv.status === "pending" || inv.status === "approved") &&
      inv.company_info?.name === "TCC"
  );

  // For revenue dialogs - filter from COMPREHENSIVE collabs
  const launchedCampaigns = comprehensiveCampaignsArray.filter(
    (c) => c.campaign_status === "Launched"
  );
  const activePartnerships = comprehensivePartnershipsArray.filter(
    (p) => p.status === "Active"
  );

  const formatAmount = (amt) =>
    amt == null
      ? "N/A"
      : `$${parseFloat(amt)
          .toFixed(2)
          .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;

  const formatCompact = (amt) => {
    if (amt == null) return "N/A";
    const num = parseFloat(amt);
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
    return `$${num.toFixed(0)}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString();
  };

  const handleRefreshAll = () => {
    refetchInvoices();
    refetchCollaborations();
    fetchDebtInstruments();
    refetchLiquidityPools();
    refetchMetrics();
    refetchComprehensiveInvoices();
    refetchComprehensiveCollabs();
  };

  // Pagination handlers
  const handleInvoicesPageChange = (event, newPage) => {
    setInvoicesPage(newPage);
  };

  const handleInvoicesRowsPerPageChange = (event) => {
    setInvoicesPerPage(parseInt(event.target.value, 10));
    setInvoicesPage(0);
  };

  const handleCollabsPageChange = (event, newPage) => {
    setCollabsPage(newPage);
  };

  const handleCollabsRowsPerPageChange = (event) => {
    setCollabsPerPage(parseInt(event.target.value, 10));
    setCollabsPage(0);
  };

  // Handlers
  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    setInvoiceDialogOpen(true);
  };

  const handleInvoiceDialogClose = (changed) => {
    setInvoiceDialogOpen(false);
    setEditingInvoice(null);
    if (changed) {
      refetchInvoices();
      refetchComprehensiveInvoices();
      refetchMetrics();
    }
  };

  const handleDeleteInvoices = () => {
    if (selectedInvoices.length > 0) deleteInvoices(selectedInvoices);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignDialogOpen(true);
  };

  const handleCampaignDialogClose = (changed) => {
    setCampaignDialogOpen(false);
    setEditingCampaign(null);
    if (changed) {
      refetchCollaborations();
      refetchComprehensiveCollabs();
      refetchMetrics();
    }
  };

  const handleEditPartnership = (partnership) => {
    setEditingPartnership(partnership);
    setPartnershipDialogOpen(true);
  };

  const handlePartnershipDialogClose = (changed) => {
    setPartnershipDialogOpen(false);
    setEditingPartnership(null);
    if (changed) {
      refetchCollaborations();
      refetchComprehensiveCollabs();
      refetchMetrics();
    }
  };

  const handleDeleteCollabs = () => {
    if (selectedCollabs.length === 0) return;

    // Separate campaigns and partnerships
    const campaignIds = [];
    const partnershipIds = [];

    selectedCollabs.forEach((id) => {
      const item = collabsArray.find((c) => c.id === id);
      if (item) {
        if (item.type === "Campaign") {
          campaignIds.push(id);
        } else {
          partnershipIds.push(id);
        }
      }
    });

    // Delete campaigns
    if (campaignIds.length > 0) {
      deleteCampaigns(campaignIds);
    }

    // Delete partnerships
    if (partnershipIds.length > 0) {
      deletePartnerships(partnershipIds);
    }
  };

  const handleEditDeliverable = (deliverable) => {
    setEditingDeliverable(deliverable);
    setDeliverableDialogOpen(true);
  };

  const handleDeliverableDialogClose = (changed) => {
    setDeliverableDialogOpen(false);
    setEditingDeliverable(null);
    if (changed) {
      refetchCollaborations();
      refetchComprehensiveCollabs();
      refetchMetrics();
    }
  };

  const handleDeleteDeliverables = () => {
    if (selectedDeliverables.length > 0)
      deleteDeliverables(selectedDeliverables);
  };

  // AR dialog handlers for BlitzPay invoices
  const handleOpenARDialog = () => {
    setARDialogMode("blitzpay");
    setARDialogOpen(true);
    setARSearchTerm("");
    setSelectedARInvoices([]);
    setSelectedCompanyFilter(null);
  };

  // Project invoices dialog handler
  const handleOpenProjectDialog = () => {
    setARDialogMode("project");
    setARDialogOpen(true);
    setARSearchTerm("");
    setSelectedARInvoices([]);
    const tccCompany = companiesArray.find((c) => c.name === "TCC");
    setSelectedCompanyFilter(tccCompany ? `company_${tccCompany.id}` : null);
  };

  const handleCloseARDialog = () => {
    setARDialogOpen(false);
    setARSearchTerm("");
    setSelectedARInvoices([]);
    setSelectedCompanyFilter(null);
    setARDialogMode("blitzpay");
  };

  const handleDeleteARInvoices = () => {
    if (selectedARInvoices.length > 0) {
      deleteInvoices(selectedARInvoices);
    }
  };

  const handleEditARInvoice = (invoice) => {
    handleEditInvoice(invoice);
  };

  const getHealthStatus = () => {
    if (overleveragedIsNegative || spendingUtilization > 90)
      return {
        label: "Critical",
        color: "error",
        icon: <WarningIcon />,
      };
    if (spendingUtilization > 75 || debtUtilization > 75)
      return {
        label: "Warning",
        color: "warning",
        icon: <WarningIcon />,
      };
    return {
      label: "Healthy",
      color: "success",
      icon: <CheckCircleIcon />,
    };
  };

  const healthStatus = getHealthStatus();

  // Invoice status filter logic
  const applyInvoiceStatusFilter = (list, filter) => {
    if (filter === "nonCanceled") {
      return list.filter((inv) => inv.status !== "canceled");
    }
    if (filter === "all") return list;
    if (filter === "canceled") {
      return list.filter((inv) => inv.status === "canceled");
    }
    return list.filter((inv) => inv.status === filter);
  };

  const invoicesAfterStatusFilter = applyInvoiceStatusFilter(
    invoicesArray,
    invoiceStatusFilter
  );

  const filteredInvoices = invoicesAfterStatusFilter.filter(
    (inv) =>
      searchTerm === "" ||
      inv.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.company_info?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Collabs filtering
  const applyCollabFilters = (list) => {
    let filtered = list;

    // Type filter
    if (collabTypeFilter === "campaigns") {
      filtered = filtered.filter((c) => c.type === "Campaign");
    } else if (collabTypeFilter === "partnerships") {
      filtered = filtered.filter((c) => c.type === "Partnership");
    }

    // Status filter
    if (collabStatusFilter !== "all") {
      filtered = filtered.filter((c) => {
        const status = c.status || c.campaign_status;
        return status?.toLowerCase() === collabStatusFilter.toLowerCase();
      });
    }

    // Search filter
    if (searchTerm !== "") {
      filtered = filtered.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.creator?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredCollabs = applyCollabFilters(collabsArray);

  const filteredDeliverables = deliverablesArray.filter(
    (del) =>
      searchTerm === "" ||
      del.deliverable_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const arInvoicesList =
    arDialogMode === "project" ? projectInvoices : pendingInvoices;
  const filteredARInvoices = arInvoicesList.filter(
    (inv) =>
      arSearchTerm === "" ||
      inv.po_number?.toLowerCase().includes(arSearchTerm.toLowerCase()) ||
      inv.company_info?.name?.toLowerCase().includes(arSearchTerm.toLowerCase()) ||
      inv.client_name?.toLowerCase().includes(arSearchTerm.toLowerCase()) ||
      inv.campaign_name?.toLowerCase().includes(arSearchTerm.toLowerCase())
  );

  // CSV Export handlers
  const handleExportInvoices = () => {
    const data = prepareInvoicesForExport(filteredInvoices);
    const timestamp = new Date().toISOString().split("T")[0];
    exportToCSV(data, `invoices_${timestamp}.csv`);
  };

  const handleExportCollabs = () => {
    const data = prepareCollabsForExport(filteredCollabs);
    const timestamp = new Date().toISOString().split("T")[0];
    exportToCSV(data, `collabs_${timestamp}.csv`);
  };

  const handleExportDeliverables = () => {
    const data = prepareDeliverablesForExport(filteredDeliverables);
    const timestamp = new Date().toISOString().split("T")[0];
    exportToCSV(data, `deliverables_${timestamp}.csv`);
  };

  return (
    <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
      <AlertDialog alertState={dialogState} />

      {/* Header */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid #e0e0e0",
          px: 4,
          py: 2.5,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#1a1a1a", mb: 0.5 }}
            >
              System Overview
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                icon={healthStatus.icon}
                label={`System ${healthStatus.label}`}
                color={healthStatus.color}
                size="small"
                sx={{ fontWeight: 600 }}
              />
              <Typography variant="caption" color="text.secondary">
                Last updated: {new Date().toLocaleTimeString()}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefreshAll}
              sx={{ borderRadius: 2 }}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 4, py: 3 }}>
        {/* Key Metrics Dashboard */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <Card
            sx={{
              borderRadius: 3,
              height: "100%",
              position: "relative",
              overflow: "visible",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
            onClick={() => setLiquidityPoolDialogOpen(true)}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Reserve Liquidity
                      </Typography>
                      <Tooltip
                        title="The total capital reserved in Blitz's treasury against lent amount. This is the baseline funding available to support all operations, including BlitzPay lines and collabs funding. Click to view and manage liquidity pools."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    {overleveragedIsNegative ? (
                      <WarningIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                    ) : null}
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#2563eb", mb: 0.5 }}
                  >
                    {formatCompact(totalLiquidity)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    {formatAmount(totalLiquidity)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", fontSize: "0.75rem" }}
                  >
                    {liquidityPoolsArray.filter((p) => p.status === "active").length}{" "}
                    active {liquidityPoolsArray.filter((p) => p.status === "active").length === 1 ? "pool" : "pools"}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              height: "100%",
              position: "relative",
              overflow: "visible",
              cursor: companiesWithSpendingPower.length > 0 ? "pointer" : "default",
              transition: "all 0.2s ease-in-out",
              "&:hover":
                companiesWithSpendingPower.length > 0
                  ? {
                      transform: "translateY(-2px)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }
                  : {},
            }}
            onClick={() => {
              if (companiesWithSpendingPower.length > 0) {
                setSpendingPowerDialogOpen(true);
              }
            }}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Spending Power Allocated
                      </Typography>
                      <Tooltip
                        title="Total nominal spending power allocated to BlitzPay companies. This represents committed credit lines that companies can draw against for their creator payments. Click to view breakdown by company."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#10b981", mb: 0.5 }}
                  >
                    {formatCompact(spendingPowerAllocated)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    {formatAmount(spendingPowerAllocated)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", fontSize: "0.875rem" }}
                  >
                    {`${spendingUtilization.toFixed(1)}% utilization`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(spendingUtilization, 100)}
                    sx={{
                      mt: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "#e5e7eb",
                      "& .MuiLinearProgress-bar": {
                        bgcolor:
                          spendingUtilization > 80
                            ? "#dc2626"
                            : spendingUtilization > 60
                            ? "#f59e0b"
                            : "#10b981",
                      },
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              height: "100%",
              position: "relative",
              overflow: "visible",
            }}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Available Spending Power
                      </Typography>
                      <Tooltip
                        title="Remaining debt capacity available to distribute as new spending power lines. Calculated as: Total Debt Capacity - Spending Power Allocated. This shows how much additional spending power can be extended to companies."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#8b5cf6", mb: 0.5 }}
                  >
                    {formatCompact(spendingPowerAvailable)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    {formatAmount(spendingPowerAvailable)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", fontSize: "0.875rem" }}
                  >
                    To distribute
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              height: "100%",
              position: "relative",
              overflow: "visible",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              },
            }}
            onClick={() => setDebtDialogOpen(true)}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Debt In Service
                      </Typography>
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              Outstanding balance on institutional debt facilities
                              (credit lines, loans, etc.).
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              <strong>Note:</strong> Other forms of debt outside of
                              institutional debt service (such as operational
                              financing or bridge capital) will be used to fund
                              collabs operations.
                            </Typography>
                          </Box>
                        }
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "#f59e0b", mb: 0.5 }}
                  >
                    {formatCompact(debtOutstanding)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#9ca3af",
                      display: "block",
                      mb: 1,
                    }}
                  >
                    {formatAmount(debtOutstanding)}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "#6b7280", fontSize: "0.875rem" }}
                  >
                    {`${debtUtilization.toFixed(1)}% of capacity`}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(debtUtilization, 100)}
                    sx={{
                      mt: 1,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: "#e5e7eb",
                      "& .MuiLinearProgress-bar": {
                        bgcolor:
                          debtUtilization > 80
                            ? "#dc2626"
                            : debtUtilization > 60
                            ? "#f59e0b"
                            : "#f59e0b",
                      },
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Financial Overview Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                borderColor: "#3b82f6",
              },
            }}
            onClick={handleOpenARDialog}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        BlitzPay AR
                      </Typography>
                      <Tooltip
                        title="Total accounts receivable from BlitzPay invoices (pending and approved). These are invoices issued to brands that will be paid to creators once collected."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "#1a1a1a", my: 1 }}
                    >
                      {formatCompact(pendingInvoicesSumTotal)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {pendingInvoicesCountTotal} invoices
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: "#3b82f615",
                      color: "#3b82f6",
                      width: 48,
                      height: 48,
                      fontSize: "1.5rem",
                    }}
                  >
                    💰
                  </Avatar>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                borderColor: "#6366f1",
              },
            }}
            onClick={handleOpenProjectDialog}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Collabs Invoices
                      </Typography>
                      <Tooltip
                        title="Invoices for TCC (Internal) projects. These are typically internal or special project invoices separate from standard BlitzPay operations."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "#1a1a1a", my: 1 }}
                    >
                      {formatCompact(projectInvoicesSumTotal)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {projectInvoicesCountTotal} TCC invoices
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: "#6366f115",
                      color: "#6366f1",
                      width: 48,
                      height: 48,
                      fontSize: "1.5rem",
                    }}
                  >
                    🔧
                  </Avatar>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                borderColor: "#8b5cf6",
              },
            }}
            onClick={() => setCollabsRevenueDialogOpen(true)}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Collabs Revenue
                      </Typography>
                      <Tooltip
                        title="Total revenue from active collabs projects. Includes launched campaigns and active partnerships. This represents committed brand spending flowing through the platform."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "#1a1a1a", my: 1 }}
                    >
                      {formatCompact(collabsRevenue)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {collabsCount} active projects
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: "#8b5cf615",
                      color: "#8b5cf6",
                      width: 48,
                      height: 48,
                      fontSize: "1.5rem",
                    }}
                  >
                    🚀
                  </Avatar>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              bgcolor: "white",
              border: "1px solid #e5e7eb",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                borderColor: "#10b981",
              },
            }}
            onClick={() => setPendingProfitsDialogOpen(true)}
          >
            <CardContent>
              {metricsLoading ? (
                <Box
                  sx={{
                    minHeight: 80,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#6b7280",
                          fontWeight: 500,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        Est. Pending Profits
                      </Typography>
                      <Tooltip
                        title="Expected profit from active collabs. Calculated as 25% of campaign revenue + 10% of partnership revenue. These margins represent Blitz's take rate on collabs transactions."
                        arrow
                        placement="top"
                      >
                        <InfoIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                      </Tooltip>
                    </Box>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 700, color: "#1a1a1a", my: 1 }}
                    >
                      {formatCompact(pendingProfits)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {collabsCount} from collabs
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: "#10b98115",
                      color: "#10b981",
                      width: 48,
                      height: 48,
                      fontSize: "1.5rem",
                    }}
                  >
                    📈
                  </Avatar>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* System Health Indicators */}
        {(overleveragedIsNegative ||
          spendingUtilization > 80 ||
          debtUtilization > 80) && (
          <Card
            sx={{
              mb: 3,
              border: "1px solid #fbbf24",
              bgcolor: "#fffbeb",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <WarningIcon sx={{ color: "#f59e0b", fontSize: 32 }} />
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "#92400e" }}
                  >
                    System Alerts
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, color: "#92400e" }}>
                    {overleveragedIsNegative && (
                      <li>
                        <Typography variant="body2">
                          System is overleveraged by{" "}
                          {formatAmount(Math.abs(overleveraged))}
                        </Typography>
                      </li>
                    )}
                    {spendingUtilization > 80 && (
                      <li>
                        <Typography variant="body2">
                          Spending power utilization is high at{" "}
                          {spendingUtilization.toFixed(1)}%
                        </Typography>
                      </li>
                    )}
                    {debtUtilization > 80 && (
                      <li>
                        <Typography variant="body2">
                          Debt utilization is high at{" "}
                          {debtUtilization.toFixed(1)}%
                        </Typography>
                      </li>
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Data Tables Section */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
            <Tabs
              value={activeTab}
              onChange={(e, val) => {
                setActiveTab(val);
                setSearchTerm("");
                setSelectedInvoices([]);
                setSelectedCollabs([]);
                setSelectedDeliverables([]);
                setCollabTypeFilter("all");
                setCollabStatusFilter("all");
                setInvoicesPage(0);
                setCollabsPage(0);
              }}
              sx={{ px: 2 }}
            >
              <Tab label={`Invoices (${totalInvoices})`} />
              <Tab label={`Collabs (${totalCollabs})`} />
              <Tab label={`Deliverables (${totalDeliverables})`} />
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Toolbar */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 260 }}
                />

                {activeTab === 0 && (
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Status Filter</InputLabel>
                    <Select
                      label="Status Filter"
                      value={invoiceStatusFilter}
                      onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                    >
                      <MenuItem value="nonCanceled">
                        Active (hide canceled)
                      </MenuItem>
                      <MenuItem value="all">All statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="canceled">Canceled only</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {activeTab === 1 && (
                  <>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={collabTypeFilter}
                        onChange={(e) => setCollabTypeFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="campaigns">Campaigns</MenuItem>
                        <MenuItem value="partnerships">Partnerships</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={collabStatusFilter}
                        onChange={(e) => setCollabStatusFilter(e.target.value)}
                      >
                        <MenuItem value="all">All Statuses</MenuItem>
                        <MenuItem value="launched">Launched</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                {activeTab === 0 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportInvoices}
                      disabled={filteredInvoices.length === 0}
                    >
                      Export CSV
                    </Button>
                    {selectedInvoices.length > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteInvoices}
                      >
                        Delete ({selectedInvoices.length})
                      </Button>
                    )}
                  </>
                )}
                {activeTab === 1 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportCollabs}
                      disabled={filteredCollabs.length === 0}
                    >
                      Export CSV
                    </Button>
                    {selectedCollabs.length > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteCollabs}
                      >
                        Delete ({selectedCollabs.length})
                      </Button>
                    )}
                  </>
                )}
                {activeTab === 2 && (
                  <>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportDeliverables}
                      disabled={filteredDeliverables.length === 0}
                    >
                      Export CSV
                    </Button>
                    {selectedDeliverables.length > 0 && (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteDeliverables}
                      >
                        Delete ({selectedDeliverables.length})
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>

            {/* Tables */}
            {activeTab === 0 && (
              <InvoicesTable
                filteredInvoices={filteredInvoices}
                selectedInvoices={selectedInvoices}
                setSelectedInvoices={setSelectedInvoices}
                handleEditInvoice={handleEditInvoice}
                formatAmount={formatAmount}
                formatDate={formatDate}
                loading={invoicesLoading}
                page={invoicesPage}
                rowsPerPage={invoicesPerPage}
                totalCount={totalInvoices}
                onPageChange={handleInvoicesPageChange}
                onRowsPerPageChange={handleInvoicesRowsPerPageChange}
              />
            )}

            {activeTab === 1 && (
              <CollabsTable
                filteredCollabs={filteredCollabs}
                selectedCollabs={selectedCollabs}
                setSelectedCollabs={setSelectedCollabs}
                handleEditCampaign={handleEditCampaign}
                handleEditPartnership={handleEditPartnership}
                formatAmount={formatAmount}
                formatDate={formatDate}
                loading={collabsLoading}
                page={collabsPage}
                rowsPerPage={collabsPerPage}
                totalCount={totalCollabs}
                onPageChange={handleCollabsPageChange}
                onRowsPerPageChange={handleCollabsRowsPerPageChange}
              />
            )}

            {activeTab === 2 && (
              <DeliverablesTable
                filteredDeliverables={filteredDeliverables}
                selectedDeliverables={selectedDeliverables}
                setSelectedDeliverables={setSelectedDeliverables}
                handleEditDeliverable={handleEditDeliverable}
                formatAmount={formatAmount}
                formatDate={formatDate}
                loading={collabsLoading}
                page={collabsPage}
                rowsPerPage={collabsPerPage}
                totalCount={totalDeliverables}
                onPageChange={handleCollabsPageChange}
                onRowsPerPageChange={handleCollabsRowsPerPageChange}
              />
            )}
          </Box>
        </Card>
      </Box>

      {/* Dialogs */}
      <Dialog
        open={isSpendingPowerDialogOpen}
        onClose={() => setSpendingPowerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FlashOnIcon sx={{ color: "#10b981" }} />
              <Typography variant="h6">Spending Power Allocated</Typography>
            </Box>
            <IconButton
              edge="end"
              onClick={() => setSpendingPowerDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {companiesWithSpendingPower.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography color="text.secondary">
                No companies with spending power allocated
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "rgba(16, 185, 129, 0.1)",
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  Total Spending Power Allocated
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, color: "#10b981" }}
                >
                  {formatAmount(spendingPowerAllocated)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  across {companiesWithSpendingPower.length}{" "}
                  {companiesWithSpendingPower.length === 1
                    ? "company"
                    : "companies"}
                </Typography>
              </Box>
              <List>
                {companiesWithSpendingPower.map((company) => (
                  <ListItem key={company.id} disablePadding>
                    <ListItemButton
                      sx={{
                        borderRadius: 1,
                        mb: 1,
                        "&:hover": {
                          backgroundColor: "rgba(16, 185, 129, 0.08)",
                        },
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar
                          src={company.phphref || ""}
                          sx={{ bgcolor: "#10b981" }}
                        >
                          {company.name?.[0]?.toUpperCase() || "C"}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{ fontWeight: 600 }}
                            >
                              {company.name}
                            </Typography>
                            <FlashOnIcon
                              sx={{ width: 16, height: 16, color: "#10b981" }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: "#10b981" }}
                            >
                              {formatAmount(
                                company.spending_power_limit
                              )}
                            </Typography>
                            {company.sub_account && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Sub-Account: {company.sub_account}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AccountsReceivableDialog
        open={isARDialogOpen}
        onClose={handleCloseARDialog}
        invoices={filteredARInvoices}
        selectedInvoices={selectedARInvoices}
        setSelectedInvoices={setSelectedARInvoices}
        searchTerm={arSearchTerm}
        setSearchTerm={setARSearchTerm}
        onEdit={handleEditARInvoice}
        onDelete={handleDeleteARInvoices}
        formatAmount={formatAmount}
        formatDate={formatDate}
        selectedCompanyFilter={selectedCompanyFilter}
        setSelectedCompanyFilter={setSelectedCompanyFilter}
        loading={comprehensiveInvoicesLoading}
        dialogMode={arDialogMode}
      />

      <CollabsRevenueDialog
        open={isCollabsRevenueDialogOpen}
        onClose={() => setCollabsRevenueDialogOpen(false)}
        campaigns={launchedCampaigns}
        partnerships={activePartnerships}
        onEditCampaign={handleEditCampaign}
        onEditPartnership={handleEditPartnership}
        formatAmount={formatAmount}
        formatDate={formatDate}
        loading={comprehensiveCollabsLoading}
      />

      <PendingProfitsDialog
        open={isPendingProfitsDialogOpen}
        onClose={() => setPendingProfitsDialogOpen(false)}
        campaigns={launchedCampaigns}
        partnerships={activePartnerships}
        onEditCampaign={handleEditCampaign}
        onEditPartnership={handleEditPartnership}
        formatAmount={formatAmount}
        formatDate={formatDate}
        loading={comprehensiveCollabsLoading}
      />

      <LiquidityPoolDialog
        open={liquidityPoolDialogOpen}
        pools={liquidityPoolsArray}
        onCreate={createLiquidityPool}
        onUpdate={updateLiquidityPool}
        onDelete={deleteLiquidityPool}
        onClose={() => setLiquidityPoolDialogOpen(false)}
      />

      <DebtDialog
        open={debtDialogOpen}
        instruments={debtInstruments}
        onCreate={createDebt}
        onUpdate={updateDebt}
        onClose={() => setDebtDialogOpen(false)}
      />

      {isInvoiceDialogOpen && (
        <InvoiceDialog
          open={isInvoiceDialogOpen}
          onClose={handleInvoiceDialogClose}
          invoiceInfo={editingInvoice}
        />
      )}
      {isCampaignDialogOpen && (
        <CampaignDialog
          open={isCampaignDialogOpen}
          info={editingCampaign}
          onClose={handleCampaignDialogClose}
        />
      )}
      {isPartnershipDialogOpen && (
        <PartnershipDialog
          open={isPartnershipDialogOpen}
          info={editingPartnership}
          onClose={handlePartnershipDialogClose}
        />
      )}
      {isDeliverableDialogOpen && (
        <DeliverableDialog
          open={isDeliverableDialogOpen}
          info={editingDeliverable}
          onClose={handleDeliverableDialogClose}
        />
      )}
    </Box>
  );
};

export default BlitzSystemOverview;